<template>
<div>
    <Breadcrumbs main="Admin" submain="Resources" title="Monitoring Data" />
    <!-- Container-fluid starts-->
    <div class="container-fluid">
        <div class="row">
            <div class="col-md-12">
                <div class="card">
                    <div class="card-body">
                        <b-row>
                            <b-col xl="6">
                                <b-input-group class="datatable-btn">
                                    <b-form-input v-model="filters.search_param" placeholder="Type to Search" @input="onSearchInput"></b-form-input>
                                    <b-input-group-append>
                                        <b-button :disabled="!filters.search_param" @click="clearSearch">Clear</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-col>

                            <b-col xl="6">
                                <b-form-group label-cols="2" label="Per page" class="mb-0 datatable-select">
                                    <b-form-select :value="perPage" :options="pageOptions" @change="changePerPage"></b-form-select>
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <b-row class="mt-3 align-items-center">
                            <b-col xl="6">
                                <b-input-group class="date-range">
                                    <b-input-group-prepend is-text>From</b-input-group-prepend>
                                    <b-form-input type="date" v-model="filters.start_date" :max="filters.end_date || null" @change="applyFilters({ start_date: filters.start_date })"></b-form-input>
                                    <b-input-group-prepend is-text class="date-range-to">To</b-input-group-prepend>
                                    <b-form-input type="date" v-model="filters.end_date" :min="filters.start_date || null" @change="applyFilters({ end_date: filters.end_date })"></b-form-input>
                                    <b-input-group-append>
                                        <b-button :disabled="!filters.start_date && !filters.end_date" @click="applyFilters({ start_date: '', end_date: '' })">Clear</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-col>

                            <b-col xl="6" class="text-xl-right mt-3 mt-xl-0">
                                <b-button variant="success" :disabled="exporting || !totalRows" @click="downloadExcel">
                                    <b-spinner v-if="exporting" small class="mr-1"></b-spinner>
                                    <feather v-else type="download" size="14px" class="mr-1" style="vertical-align: -2px;"></feather>
                                    {{ exporting ? 'Preparing...' : 'Download Excel' }}
                                </b-button>
                            </b-col>
                        </b-row>

                        <div v-if="!loading && !error">
                            <div class="table-responsive datatable-vue mt-4">
                                <b-table show-empty stacked="md" :items="rows" :fields="tablefields">
                                    <template #cell(index)="data">
                                        {{ (currentPage - 1) * perPage + data.index + 1 }}
                                    </template>

                                    <template #cell(ast_ID)="row">
                                        <router-link v-if="row.item.ast_ID" :to="'/resources/asset/detail/' + row.item.ast_ID">{{ row.item.ast_ID }}</router-link>
                                    </template>

                                    <template #cell(status)="row">
                                        <b-badge v-if="row.item.status" pill :variant="statusVariant(row.item.status)">{{ row.item.status }}</b-badge>
                                    </template>

                                    <template #cell(type)="row">
                                        <h7 v-if="row.item.type">{{ row.item.type }}</h7>
                                    </template>

                                    <template #cell(last_monitored)="row">
                                        <h7 v-if="row.item.last_monitored">{{ formatDate(row.item.last_monitored).date }} | {{ formatDate(row.item.last_monitored).time }}</h7>
                                    </template>

                                    <template #cell(action)="row">
                                        <feather type="message-square" size="20px" style="color: #2c323f; cursor: pointer;" v-b-tooltip.hover title="View Remarks" @click="viewRemarks(row.item)"></feather>
                                    </template>
                                </b-table>
                            </div>
                        </div>

                        <div v-else-if="loading">
                            <loader />
                        </div>

                        <div v-else class="text-center text-danger my-4">
                            Failed to load monitoring data.
                        </div>

                        <b-col md="6" class="my-1 p-0">
                            <b-pagination :value="currentPage" :total-rows="totalRows" :per-page="perPage" class="my-0" @change="goToPage"></b-pagination>
                        </b-col>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- Container-fluid Ends-->

    <!-- Remarks Modal -->
    <b-modal id="modal-remarks" :title="selected ? 'Remarks — ' + (selected.name || selected.ast_ID) : 'Remarks'" size="lg" ok-only ok-title="Close" scrollable>
        <div v-if="selected && selected.remarks.length" class="accordion">
            <div v-for="(remark, index) in selected.remarks" :key="remark.id || index" class="acc-item" :class="{ open: openIndex === index }">
                <div class="acc-header" @click="toggle(index)">
                    <div style="display: flex; align-items: center; gap: 12px;">
                        <span v-if="remark.created_at">{{ formatDate(remark.created_at).date }} | {{ formatDate(remark.created_at).time }}</span>
                        <b-badge v-if="remark.status" pill :variant="statusVariant(remark.status)">{{ remark.status }}</b-badge>
                    </div>
                    <svg class="acc-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="3,6 8,11 13,6" />
                    </svg>
                </div>

                <transition name="accordion">
                    <div v-show="openIndex === index">
                        <div class="acc-content">
                            <p v-if="remark.checked_by" style="font-size: 14px;">Checked By: <strong><router-link :to="'/volunteer/detail/' + remark.checked_by.id">{{ remark.checked_by.name }} <span v-if="remark.checked_by.volunteer_id">({{ remark.checked_by.volunteer_id }})</span></router-link></strong></p>
                            <p v-if="remark.remark" style="font-size: 14px; white-space: pre-line;">{{ remark.remark }}</p>
                            <p style="font-size: 14px;" v-if="remark.parameter">Parameter: <strong>{{ remark.parameter }}</strong></p>
                            <div v-if="remark.image">
                                <img style="max-width: 280px;" :src="remark.image">
                            </div>
                            <ul v-if="remark.items.length" class="feature-list">
                                <li v-for="(feature, i) in remark.items" :key="feature.id || i" class="feature-item">
                                    <span class="feature-icon" :class="feature.response ? 'icon-tick' : 'icon-cross'">
                                        <svg v-if="feature.response" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <polyline points="2.5,8.5 6,12 13.5,4" />
                                        </svg>
                                        <svg v-else viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <line x1="4" y1="4" x2="12" y2="12" />
                                            <line x1="12" y1="4" x2="4" y2="12" />
                                        </svg>
                                    </span>
                                    <span class="feature-label">{{ feature.feature }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
        <div v-else class="text-center text-muted my-3">No remarks found.</div>
    </b-modal>
</div>
</template>

<script>
import { mapState } from 'vuex';
import { myHost } from '../../../_helpers/host';
import loader from '../loader/loader.vue';
import routePagination from '../../../_helpers/routePagination';
import Swal from 'sweetalert2/dist/sweetalert2.js';

const STATUS_VARIANTS = {
    'APPROVAL PENDING': 'info',
    'UNDER MAINTENANCE': 'warning',
    'ACTIVE': 'success',
    'INACTIVE': 'danger'
};

const typeName = (type) => (type && typeof type === 'object' ? type.name : type) || '';

const locationText = (loc) => {
    if (!loc) return '';
    if (typeof loc !== 'object') return loc;
    return [loc.name, loc.camp?.camp_name, loc.area?.area_name, loc.district?.district_name, loc.country?.country_name].filter(Boolean).join(', ');
};

// The checked items of one monitoring entry ({feature, response} objects)
const checklistItems = (entry) => {
    const list = [entry.items, entry.checklist_items, entry.checklist, entry.checklists].find(Array.isArray) || [];
    return list.map(f => ({ ...f, feature: f.feature?.feature || f.feature?.name || f.feature || f.name, response: !!f.response }));
};

// Monitoring entries for a record: a history list when the API sends one, otherwise the record itself
const monitoringEntries = (item) => {
    const isEntryList = (list) => Array.isArray(list) && list.some(e => e && (e.remark !== undefined || e.items || e.checked_by));
    const list = [item.remarks?.history, item.remarks, item.checklists, item.checklist].find(isEntryList);
    const entries = list || (item.remark || checklistItems(item).length ? [item] : []);
    return entries.map(entry => ({ ...entry, items: checklistItems(entry) }));
};

// Flatten a record so the table and the Excel export work on plain fields.
// Asset info may come nested under `asset` or at the top level.
const toRow = (item) => {
    const asset = item.asset || item;

    return {
        name: asset.name,
        ast_ID: asset.ast_ID,
        status: item.status || asset.status,
        type: typeName(item.type || asset.type),
        location: locationText(item.location || item.location_details || asset.location || asset.location_details),
        last_monitored: item.last_monitored || item.last_monitoring_date || item.created_at || item.updated_at,
        remarks: monitoringEntries(item)
    };
};

const EXCEL_HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2C323F' } };
const EXCEL_GROUP_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F7FB' } };
const EXCEL_IMAGE_MAX = 120; // px, longest side of an embedded remark image
const EXCEL_IMAGE_CONCURRENCY = 6;
// Every data row gets the height of an image row (points = 0.75 per px, plus padding)
const EXCEL_ROW_HEIGHT = EXCEL_IMAGE_MAX * 0.75 + 8;

const imageUrl = (path) => (/^(https?:|data:|blob:)/.test(path) ? path : myHost.host + path);

// Downloads an image and shrinks it to a JPEG thumbnail; resolves null if it can't be loaded
const loadThumbnail = async (path) => {
    try {
        const response = await fetch(imageUrl(path));
        if (!response.ok) return null;
        const bitmap = await createImageBitmap(await response.blob());
        const scale = Math.min(1, EXCEL_IMAGE_MAX / Math.max(bitmap.width, bitmap.height));
        const width = Math.round(bitmap.width * scale);
        const height = Math.round(bitmap.height * scale);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff'; // JPEG has no transparency
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(bitmap, 0, 0, width, height);
        bitmap.close && bitmap.close();

        return { base64: canvas.toDataURL('image/jpeg', 0.85), width, height };
    } catch (e) {
        return null;
    }
};

// Loads thumbnails for the given paths a few at a time; returns a Map of path -> thumbnail|null
const loadThumbnails = async (paths) => {
    const unique = [...new Set(paths)];
    const results = new Map();
    let next = 0;
    const worker = async () => {
        while (next < unique.length) {
            const path = unique[next++];
            results.set(path, await loadThumbnail(path));
        }
    };
    await Promise.all(Array.from({ length: Math.min(EXCEL_IMAGE_CONCURRENCY, unique.length) }, worker));
    return results;
};

export default {
    mixins: [routePagination],

    components: {
        loader
    },

    data() {
        return {
            host: myHost.host,
            tablefields: [
                { key: 'index', label: '#' },
                { key: 'name', label: 'Asset Name', sortable: true },
                { key: 'ast_ID', label: 'Asset ID', sortable: true },
                { key: 'status', label: 'Status', sortable: true },
                { key: 'type', label: 'Type', sortable: true },
                { key: 'location', label: 'Location', sortable: false },
                { key: 'last_monitored', label: 'Last Monitoring Date', sortable: true },
                { key: 'action', label: 'Remarks', sortable: false }
            ],
            routeFilterKeys: ['search_param', 'start_date', 'end_date'],
            selected: null,
            openIndex: 0,
            exporting: false
        };
    },

    computed: {
        ...mapState({
            checklist: state => state.asset.checklist,
            totalRows: state => state.asset.checklistTotal,
            loading: state => state.asset.checklistLoading,
            error: state => state.asset.checklistError
        }),

        rows() {
            return this.checklist.map(toRow);
        }
    },

    methods: {
        fetchPage(params) {
            this.$store.dispatch("asset/fetchChecklist", params)
        },

        statusVariant(status) {
            return STATUS_VARIANTS[status] || 'secondary';
        },

        viewRemarks(row) {
            this.selected = row;
            this.openIndex = 0;
            this.$bvModal.show('modal-remarks');
        },

        toggle(index) {
            this.openIndex = index;
        },

        formatDateTime(dateStr) {
            const formatted = this.formatDate(dateStr);
            return formatted ? `${formatted.date} | ${formatted.time}` : '';
        },

        // Exports every record matching the applied search/date filters (not just this page).
        // Filters are read from the route so the file matches what the table shows.
        async downloadExcel() {
            this.exporting = true;
            try {
                const active = {};
                this.routeFilterKeys.forEach(key => {
                    if (this.$route.query[key]) active[key] = this.$route.query[key];
                });

                const [records, { default: ExcelJS }] = await Promise.all([
                    this.$store.dispatch('asset/fetchAllChecklist', active),
                    import(/* webpackChunkName: "exceljs" */ 'exceljs')
                ]);
                const rows = records.map(toRow);
                const thumbnails = await loadThumbnails(
                    rows.flatMap(row => row.remarks.map(remark => remark.image).filter(Boolean))
                );

                const workbook = new ExcelJS.Workbook();
                const sheet = workbook.addWorksheet('Monitoring Data');
                sheet.columns = [
                    { header: '#', key: 'index', width: 6 },
                    { header: 'Asset Name', key: 'name', width: 28 },
                    { header: 'Asset ID', key: 'ast_ID', width: 16 },
                    { header: 'Status', key: 'status', width: 20 },
                    { header: 'Type', key: 'type', width: 18 },
                    { header: 'Location', key: 'location', width: 45 },
                    { header: 'Remark Date', key: 'remark_date', width: 28 },
                    { header: 'Remark Status', key: 'remark_status', width: 20 },
                    { header: 'Checked By', key: 'checked_by', width: 28 },
                    { header: 'Remark', key: 'remark', width: 50 },
                    { header: 'Parameter', key: 'parameter', width: 16 },
                    { header: 'Checklist', key: 'items', width: 45 },
                    { header: 'Image', key: 'image', width: 20 }
                ];
                const imageCol = sheet.columnCount - 1; // zero-based, for addImage

                // One line per remark; the asset columns repeat so Excel's filters keep working.
                // Alternate assets are shaded so each asset's remarks read as one group.
                rows.forEach((row, i) => {
                    const asset = {
                        index: i + 1,
                        name: row.name,
                        ast_ID: row.ast_ID,
                        status: row.status,
                        type: row.type,
                        location: row.location
                    };
                    const entries = row.remarks.length ? row.remarks : [null];

                    entries.forEach(remark => {
                        const by = remark?.checked_by;
                        const line = sheet.addRow({
                            ...asset,
                            remark_date: remark ? this.formatDateTime(remark.created_at) : '',
                            remark_status: remark?.status || '',
                            checked_by: by ? by.name + (by.volunteer_id ? ` (${by.volunteer_id})` : '') : '',
                            remark: remark?.remark || '',
                            parameter: remark?.parameter || '',
                            items: (remark?.items || []).map(f => `${f.response ? '✓' : '✗'} ${f.feature}`).join('\n')
                        });
                        line.alignment = { vertical: 'top', wrapText: true };
                        line.height = EXCEL_ROW_HEIGHT;
                        if (i % 2) line.fill = EXCEL_GROUP_FILL;

                        if (remark?.image) {
                            const thumb = thumbnails.get(remark.image);
                            if (thumb) {
                                const imageId = workbook.addImage({ base64: thumb.base64, extension: 'jpeg' });
                                sheet.addImage(imageId, {
                                    tl: { col: imageCol + 0.05, row: line.number - 1 + 0.05 },
                                    ext: { width: thumb.width, height: thumb.height },
                                    editAs: 'oneCell'
                                });
                            } else {
                                // Couldn't embed it; link to the original instead
                                const cell = line.getCell('image');
                                cell.value = { text: 'View image', hyperlink: imageUrl(remark.image) };
                                cell.font = { color: { argb: 'FF0563C1' }, underline: true };
                            }
                        }
                    });
                });

                const header = sheet.getRow(1);
                header.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                header.fill = EXCEL_HEADER_FILL;
                header.alignment = { vertical: 'middle' };
                header.height = 20;
                sheet.views = [{ state: 'frozen', ySplit: 1 }];
                sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: sheet.columnCount } };

                const buffer = await workbook.xlsx.writeBuffer();
                this.saveFile(buffer, this.exportFileName());

            } catch (e) {
                Swal.fire({
                    title: "Error!",
                    text: "Failed to download monitoring data. Please try again.",
                    icon: "error"
                });
            } finally {
                this.exporting = false;
            }
        },

        exportFileName() {
            const { start_date, end_date } = this.$route.query;
            const today = new Date().toISOString().slice(0, 10);
            const range = start_date || end_date ? `${start_date || 'start'}_to_${end_date || today}` : today;
            return `monitoring-data_${range}.xlsx`;
        },

        saveFile(buffer, fileName) {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        },

        formatDate(dateStr) {
            if (!dateStr) return ''

            // Split date and time manually
            const [datePart, timePart = '00:00'] = String(dateStr).replace('Z', '').split(/[T ]/)

            const [year, month, day] = datePart.split('-')
            const [hourRaw, minute] = timePart.split(':')

            const hours = parseInt(hourRaw)

            const monthNames = [
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'
            ]

            const monthName = monthNames[parseInt(month) - 1]

            const ampm = hours >= 12 ? 'PM' : 'AM'
            const hour12 = hours % 12 || 12

            return {
                date: `${parseInt(day)} ${monthName}, ${year}`,
                time: `${hour12}:${minute} ${ampm}`
            }
        }
    }
};
</script>

<style scoped>
.date-range ::v-deep .input-group-text {
    background-color: #f5f7fb;
    font-size: 14px;
}

/* The middle "To" label sits between two inputs, so it needs square corners on both sides */
.date-range ::v-deep .date-range-to .input-group-text {
    border-radius: 0;
}

.date-range input[type="date"] {
    min-width: 0;
    font-size: 14px;
}

.date-range input[type="date"]::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
}

@media (max-width: 575px) {
    .date-range {
        flex-wrap: nowrap;
    }

    .date-range ::v-deep .input-group-text {
        padding-left: 8px;
        padding-right: 8px;
    }
}

/* Remarks accordion, matching the asset details page */
.accordion {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.acc-item {
    border: 0.5px solid #e0ddd6;
    border-radius: 6px;
    overflow: hidden;
    background: #ffffff;
    transition: border-color 0.2s;
}

.acc-item.open {
    border-color: #c4c1b8;
}

.acc-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    cursor: pointer;
    user-select: none;
    gap: 12px;
}

.acc-header:hover {
    background: #f5f4f0;
}

.acc-chevron {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: #999;
    transition: transform 0.25s ease;
}

.acc-item.open .acc-chevron {
    transform: rotate(180deg);
}

.acc-content {
    padding: 14px 18px 16px;
    border-top: 0.5px solid #e0ddd6;
}

.acc-content p:last-child {
    margin-bottom: 0;
}

/* Vue 2 transition classes */
.accordion-enter-active,
.accordion-leave-active {
    transition: max-height 0.3s ease, opacity 0.25s ease;
    max-height: 600px;
    overflow: hidden;
}

.accordion-enter,
.accordion-leave-to {
    max-height: 0;
    opacity: 0;
}

.feature-list {
    list-style: none;
    margin: 12px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    border-top: 0.5px solid #e0ddd6;
    padding-top: 12px;
}

.feature-item {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
}

.feature-icon {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.feature-icon svg {
    width: 11px;
    height: 11px;
}

.icon-tick {
    background: #EAF3DE;
    color: #3B6D11;
}

.icon-cross {
    background: #FCEBEB;
    color: #A32D2D;
}
</style>
